import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  useMediaQuery,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useTheme } from "@mui/material/styles";
import { analytics } from "../firebaseConfig";
import { logEvent } from "firebase/analytics";

const isVeoMatchUrl = (url) =>
  typeof url === "string" && /^https:\/\/app\.veo\.co\/matches\//i.test(url.trim());

const normRecordUrl = (url) => (url || "").trim().replace(/\/$/, "");

// Veo thumbnails: app.veo.co blocks CORS, so we use a Supabase Edge Function that fetches the
// match page server-side and returns the og:image URL.
async function fetchVeoThumbnailViaProxy(recordUrl, proxyBase) {
  if (!proxyBase || !isVeoMatchUrl(recordUrl)) return null;
  try {
    const url = `${proxyBase}?url=${encodeURIComponent(recordUrl.trim())}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const thumb = data?.thumbnail ?? data?.ogImage ?? data?.url;
    return typeof thumb === "string" ? thumb : null;
  } catch {
    return null;
  }
}

// Default: Supabase Edge Function (CORS-safe). Override with REACT_APP_VEO_THUMBNAIL_PROXY if needed.
const DEFAULT_VEO_THUMBNAIL_PROXY =
  process.env.REACT_APP_VEO_THUMBNAIL_PROXY ||
  "https://wcoqsdbueqporwnngdab.supabase.co/functions/v1/veo-thumbnail";

const HighlightsCarousel = ({ matches }) => {
  const [videoIndices, setVideoIndices] = useState({});
  const [thumbnails, setThumbnails] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const recordingUrls = matches?.flatMap((m) => m.recordings || []) || [];
  useEffect(() => {
    const seen = new Set();
    recordingUrls.forEach((url) => {
      const key = normRecordUrl(url);
      if (!key || seen.has(key) || thumbnails[key] !== undefined) return;
      seen.add(key);
      if (!isVeoMatchUrl(key)) return;
      let cancelled = false;
      fetchVeoThumbnailViaProxy(key, DEFAULT_VEO_THUMBNAIL_PROXY).then((thumb) => {
        if (!cancelled && thumb) setThumbnails((prev) => ({ ...prev, [key]: thumb }));
      });
      return () => { cancelled = true; };
    });
  }, [recordingUrls.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!matches || matches.length === 0) return null;

  const handlePrev = (id, total) => {
    setVideoIndices((prev) => {
      const current = prev[id] || 0;
      return {
        ...prev,
        [id]: current > 0 ? current - 1 : total - 1,
      };
    });
  };

  const handleNext = (id, total) => {
    setVideoIndices((prev) => {
      const current = prev[id] || 0;
      return {
        ...prev,
        [id]: current < total - 1 ? current + 1 : 0,
      };
    });
  };

  return (
    <Box sx={{ mt: 4, px: 2 }}>
      <Typography variant="h6" sx={{ color: "#1a1a1a", mb: 2, fontWeight: 600 }}>
        Match Highlights
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          pb: 1,
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {matches.map((match) => {
          const title = `${match.home_team?.name || "Home"} vs ${
            match.away_team?.name || "Away"
          }`;
          const total = match.recordings?.length || 0;
          const index = videoIndices[match.id] || 0;

          if (total === 0) return null;

          return (
            <Card
              key={match.id}
              sx={{
                minWidth: 280,
                backgroundColor: "#fff",
                color: "#1a1a1a",
                borderRadius: 2,
                border: "1px solid #eee",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: "0.85rem", mb: 1 }}
                >
                  {title}
                </Typography>

                {!isMobile ? (
                  <Box sx={{ position: "relative" }}>
                    <Box
                      onClick={() => {
                        const url = match.recordings[index];
                        window.open(url, "_blank");
                        logEvent(analytics, "video_click", {
                          match_id: match.id,
                          team_home: match.home_team?.name,
                          team_away: match.away_team?.name,
                          recording_index: index + 1,
                          city: match.city,
                        });
                      }}
                      sx={{
                        height: 160,
                        borderRadius: 1,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        cursor: "pointer",
                        border: "1px dashed #e0e0e0",
                        overflow: "hidden",
                      }}
                    >
                      {thumbnails[normRecordUrl(match.recordings[index])] ? (
                        <>
                          <Box
                            component="img"
                            src={thumbnails[normRecordUrl(match.recordings[index])]}
                            alt=""
                            onError={() => {
                              const k = normRecordUrl(match.recordings[index]);
                              setThumbnails((prev) => {
                                const next = { ...prev };
                                delete next[k];
                                return next;
                              });
                            }}
                            sx={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <PlayCircleOutlineIcon
                            sx={{
                              position: "relative",
                              zIndex: 1,
                              fontSize: 48,
                              color: "rgba(0,0,0,0.7)",
                              filter: "drop-shadow(0 1px 2px rgba(255,255,255,0.8))",
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <PlayCircleOutlineIcon
                            sx={{ fontSize: 56, color: "rgba(0,0,0,0.5)" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: "#666", mt: 0.5 }}
                          >
                            Click to watch
                          </Typography>
                        </>
                      )}
                    </Box>

                    {total > 1 && (
                      <>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrev(match.id, total);
                          }}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: 4,
                            transform: "translateY(-50%)",
                            color: "#fff",
                            zIndex: 3,
                            backgroundColor: "rgba(0,0,0,0.6)",
                            width: 40,
                            height: 40,
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.8)",
                            },
                            "& .MuiSvgIcon-root": { fontSize: 28 },
                          }}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNext(match.id, total);
                          }}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            right: 4,
                            transform: "translateY(-50%)",
                            color: "#fff",
                            zIndex: 3,
                            backgroundColor: "rgba(0,0,0,0.65)",
                            width: 40,
                            height: 40,
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.85)",
                            },
                            "& .MuiSvgIcon-root": { fontSize: 28 },
                          }}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {match.recordings.map((link, i) => (
                      <Button
                        key={i}
                        variant="outlined"
                        onClick={() => {
                          window.open(link, "_blank");
                          logEvent(analytics, "video_click", {
                            match_id: match.id,
                            team_home: match.home_team?.name,
                            team_away: match.away_team?.name,
                            recording_index: i + 1,
                            city: match.city,
                          });
                        }}
                        sx={{
                          textTransform: "none",
                          borderColor: "#90caf9",
                          color: "#90caf9",
                        }}
                      >
                        Watch {i + 1}
                      </Button>
                    ))}
                  </Box>
                )}

                {total > 1 && !isMobile && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#aaa",
                      mt: 1,
                      display: "block",
                      textAlign: "center",
                    }}
                  >
                    Recording {index + 1} of {total}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default HighlightsCarousel;
